import { Box, TableBody, TableCell, TableRow } from '@mui/material'
import { Actions, Field } from './types'
import { GenericTooltip } from '../generic-tooltip'
import { useTranslation } from 'react-i18next'
import { Skeleton } from '@material-ui/lab'

export type BodyProps<T> = {
  readonly fields: Field<T>[]
  readonly items: T[]
  readonly rowKeyField: keyof T
  readonly actions?: Actions<T>
  readonly isLoading?: boolean
}

export function Body<T extends { [key: string]: any }>(props: BodyProps<T>) {
  const { t } = useTranslation()

  const rows = props.items.map((item, i) => {
    return !props.isLoading ? (
      <TableRow key={item[props.rowKeyField] + i}>
        {Object.values(props.fields).map((field, idx) => {
          return (
            <TableCell
              style={{
                width: `${100 / Object.keys(props.fields).length}%`,
                marginLeft: 4,
              }}
              key={(field.name as string) + idx + 'cell'}
            >
              {field.renderFunc
                ? field.renderFunc(field, item)
                : item[field.name]?.toString() || ''}
            </TableCell>
          )
        })}

        {props.actions && props.actions.items?.length > 0 && (
          <TableCell
            style={{
              width: `${100 / Object.keys(props.fields).length}%`,
              marginLeft: 4,
            }}
            key={item[props.rowKeyField] + 'cell'}
          >
            <Box
              key={item[props.rowKeyField] + 'box'}
              display="flex"
              justifyContent="space-between"
              gap={1}
            >
              {props.actions.items.map((a, idx) => {
                const isHidden = a.hidden && a.hidden(item)

                if (isHidden && !props.actions?.preserveHiddenSpace) {
                  return null
                }

                return (
                  <Box
                    key={`action-${idx}`}
                    style={{
                      visibility: isHidden ? 'hidden' : 'visible',
                      display: 'inline-block',
                    }}
                  >
                    <GenericTooltip
                      values={[t(a.label || a.icon)]}
                      icon={
                        <img
                          src={a.icon}
                          key={item[props.rowKeyField]}
                          onClick={() => !isHidden && a.handler(item)}
                          style={{ pointerEvents: isHidden ? 'none' : 'auto', cursor: 'pointer' }}
                        />
                      }
                      noUseFab={true}
                    />
                  </Box>
                )
              })}
            </Box>
          </TableCell>
        )}
      </TableRow>
    ) : (
      <TableRow key={item[props.rowKeyField] + i}>
        {Object.values(props.fields).map((field, idx) => {
          return (
            <TableCell key={(field.name as string) + idx + 'cell'}>
              <Skeleton />
            </TableCell>
          )
        })}

        {props.actions && props.actions.items?.length > 0 && (
          <TableCell key={item[props.rowKeyField] + i + 'cellq'}>
            <Box display="flex" justifyContent="space-between">
              {props.actions.items.map((a, idx) => {
                if (a.hidden && a.hidden(item)) {
                  return null
                }
                return (
                  <GenericTooltip
                    key={`skeleton-${idx}`}
                    values={[t(a.label || a.icon)]}
                    icon={<Skeleton variant="circle" width={24} height={24} />}
                    noUseFab={true}
                  />
                )
              })}
            </Box>
          </TableCell>
        )}
      </TableRow>
    )
  })

  return <TableBody>{rows}</TableBody>
}
